import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Grid,
    InputLabel,
    Stack,
    TextField,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Pagination,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';
import { DataSourceDto } from 'inside-fifa/api/generic-tables/dto';
import { UpdateConfigDialogFormValues } from '../../dto';
import { useUniqueColumnValues } from 'inside-fifa/api/generic-tables/hooks/useUniqueColumnValues';

type KeyAttributeMappingsProps = {
    dataSource: DataSourceDto;
};

export const KeyAttributeMappings = ({ dataSource }: KeyAttributeMappingsProps) => {
    const { values, getFieldProps, setFieldValue } = useFormikContext<UpdateConfigDialogFormValues>();

    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Get mapping index for getValueForValue and getFieldProps
    const getMappingIndexForValue = useCallback(
        (value: string) => {
            return values.config.keyAttributeColumnValues?.findIndex((m) => m.key === value) ?? -1;
        },
        [values.config.keyAttributeColumnValues],
    );

    // Update mappings when API returns new data and handle column changes
    const lastAPIDataRef = useRef<any[]>([]);
    const currentTrackingColumn = useRef<string>('');

    // Handle key attribute column change
    useEffect(() => {
        if (values.config.keyAttributeColumn) {
            if (currentTrackingColumn.current && values.config.keyAttributeColumn !== currentTrackingColumn.current) {
                // Clear existing mappings when column changes
                setFieldValue('config.keyAttributeColumnValues', []);
                lastAPIDataRef.current = [];
            }
            currentTrackingColumn.current = values.config.keyAttributeColumn;

            // Reset pagination on column change
            setPage(1);
        }
    }, [values.config.keyAttributeColumn, setFieldValue]);

    // Only fetch when we have no mappings
    const shouldFetchFromAPI = Boolean(
        dataSource.fileId &&
            dataSource.sheetId &&
            values.config.keyAttributeColumn &&
            values.config.keyAttributeColumnValues?.length === 0,
    );

    // API call
    const {
        values: uniqueValues,
        loading,
        error,
    } = useUniqueColumnValues({
        fileId: dataSource.fileId ?? 0,
        sheetId: dataSource.sheetId ?? 0,
        columnName: values.config.keyAttributeColumn || null,
        enabled: shouldFetchFromAPI,
    });

    // Update Formik when API returns new data with proper value comparison
    useEffect(() => {
        if (uniqueValues.length > 0) {
  let hasNewValues = uniqueValues.length !== lastAPIDataRef.current.length;


if (!hasNewValues) {
    const lastValuesSet = new Set(lastAPIDataRef.current);
    hasNewValues = !uniqueValues.every((val) => lastValuesSet.has(val));
}

            if (hasNewValues) {
                lastAPIDataRef.current = [...uniqueValues];

                const newMappings = uniqueValues.map((value) => ({
                    key: value,
                    color: '#000000',
                    icon: '',
                }));

                setFieldValue('config.keyAttributeColumnValues', newMappings);
                setPage(1); // Reset pagination when new data arrives
            }
        }
    }, [uniqueValues, setFieldValue]);

    const displayValues = useMemo(() => {
        if (!values.config.keyAttributeColumn) {
            return [];
        }

        // Just show whatever values we have (from initial object or from API)
        return values.config.keyAttributeColumnValues?.map((m) => m.key) || [];
    }, [values.config.keyAttributeColumn, values.config.keyAttributeColumnValues]);

    // Show loading state (only when fetching from API)
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.loading" />
                </Typography>
            </Box>
        );
    }

    // Show error state (only when fetching from API)
    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.error" values={{ error }} />
            </Alert>
        );
    }

    // Show message when no data source is configured
    if (!dataSource.fileId || !dataSource.sheetId) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.no-data-source" />
            </Typography>
        );
    }

    // Show message when no column is selected
    if (!values.config.keyAttributeColumn) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.no-column-selected" />
            </Typography>
        );
    }

    // Show message when no values found
    if (displayValues.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.no-values" />
            </Typography>
        );
    }

    // Paginate values to prevent freezing
    const totalPages = Math.ceil(displayValues.length / ITEMS_PER_PAGE);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedValues = displayValues.slice(startIndex, endIndex);

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
                Showing {startIndex + 1}-{Math.min(endIndex, displayValues.length)} of {displayValues.length} mappings
            </Typography>

            <Grid container spacing={3}>
                {paginatedValues.map((value) => {
                    const mappingIndex = getMappingIndexForValue(value);

                    return (
                        <Grid item xs={12} key={`mapping-${value}`}>
                            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {value}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={1}>
                                            <InputLabel>
                                                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.color" />
                                            </InputLabel>
                                            <TextField
                                                type="color"
                                                {...getFieldProps(
                                                    `config.keyAttributeColumnValues[${mappingIndex}].color`,
                                                )}
                                                sx={{ width: 100, height: 40 }}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Stack spacing={1}>
                                            <InputLabel>
                                                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.icon" />
                                            </InputLabel>
                                            <TextField
                                                fullWidth
                                                {...getFieldProps(
                                                    `config.keyAttributeColumnValues[${mappingIndex}].icon`,
                                                )}
                                                placeholder="Enter icon URL"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, newPage) => setPage(newPage)}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
};
